import React, { useState, useEffect, useRef } from 'react';
import { Camp, FilterInfo } from '../../utils/types';
import { useFilterCamps } from '../../api/camps';
import './SimilarCamps.css';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { CampFormatUtils } from '../../utils/campFormatUtils';
import GoogleRating from '../GoogleRating';
import { useNavigate } from 'react-router-dom';
import { FilterOptionsUtils } from '../../utils/filterOptionsUtils';

interface SimilarCampsProps {
  currentCamp?: Camp;
  filters: FilterInfo;
  title: string;
  displayDates?: boolean | false;
  displayOrganizer?: boolean | false;
  displayRating?: boolean | false;
  oneCampPerOrganizer?: boolean | false;
  showViewAll?: boolean | false;
  onCampClick: (camp: Camp) => void;
  onViewAllClick?: () => void;
}

const SimilarCamps: React.FC<SimilarCampsProps> = ({ 
  currentCamp,
  filters,
  title,
  displayDates,
  displayOrganizer,
  displayRating,
  oneCampPerOrganizer,
  showViewAll,
  onCampClick,
  onViewAllClick
}) => {
  const navigate = useNavigate();
  const [scrollPosition, setScrollPosition] = useState(0);
  const [similarCamps, setSimilarCamps] = useState<Camp[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  const calculateMinHeight = () => {
    let height = 245;
    if (displayDates) height += 25;
    if (displayOrganizer) height += 25;
    if (displayRating) height += 25;
    return `${height}px`;
  };
  
  const filterCampsMutation = useFilterCamps();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchSimilarCamps = async () => {
      setIsLoading(true);
      try {
        const result = await filterCampsMutation.mutateAsync(filters);
        
        let filtered = result.camps;

        if (oneCampPerOrganizer) {
          filtered = Array.from(new Map(filtered.map((camp) => [camp.organizer?.id, camp])).values())
        }
        // Filter out the current camp
        if (currentCamp) {
          filtered = filtered?.filter(camp => camp.id !== currentCamp.id)
        }
        // If organizerId is not provided, filter out camps by the current camp's organizer
        if (!filters.organizer_id && currentCamp) {
          filtered = filtered?.filter(camp => camp.organizer?.id !== currentCamp.organizer?.id)
        }
        setSimilarCamps(filtered?.slice(0, 10) || []);
      } catch (err) {
        console.error('Error fetching similar camps:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSimilarCamps();
  }, [filters, currentCamp]);

  const scrollLeft = () => {
    const newPosition = Math.max(0, scrollPosition - 300);
    scrollContainerRef.current?.scrollTo({ left: newPosition, behavior: 'smooth' });
    setScrollPosition(newPosition);
  };

  const scrollRight = () => {
    const newPosition = scrollPosition + 300;
    scrollContainerRef.current?.scrollTo({ left: newPosition, behavior: 'smooth' });
    setScrollPosition(newPosition);
  };

  const getCampImage = (camp: Camp): string => {
    return camp.images.find(image => image.type === 'default')?.url || '/images/camp-card-default-image.png';
  };

  const handleViewAll = () => {
    if (onViewAllClick) {
      console.log('onViewAllClick');
      onViewAllClick();
    }
    const searchParams = FilterOptionsUtils.filtersToSearchParams(FilterOptionsUtils.convertFilterInfoToFilterOptions(filters));
    navigate(`/search?${searchParams.toString()}`);
  };

  if (isLoading) {
    return <div className="similar-camps-loading">Loading similar camps...</div>;
  }

  if (similarCamps.length === 0) {
    return null;
  }

  return (
    <div className="similar-camps">
      <div className="similar-camps-header">
        <h2 className="similar-camps-title">{title}</h2>
        {showViewAll && (
          <button className="similar-camps-view-all" onClick={handleViewAll}>
            View all
          </button>
        )}
      </div>
      
      <div className="similar-camps-container">
        <button 
          className="similar-camps-nav similar-camps-nav--left"
          onClick={scrollLeft}
          disabled={scrollPosition === 0}
        >
          <ChevronLeft size={20} />
        </button>

        <div className="similar-camps-scroll" ref={scrollContainerRef}>
          {similarCamps.map((camp) => (
            <div 
              key={camp.id} 
              className="similar-camp-card"
              style={{ minHeight: calculateMinHeight() }}
              onClick={() => onCampClick(camp)}
            >
              <img 
                src={getCampImage(camp)} 
                alt={camp.name}
                className="similar-camp-image"
              />
              <div className="similar-camp-content">
                <div className="similar-camp-main-content">
                  <h4 className="similar-camp-title">{camp.name}</h4>
                  {displayOrganizer && <p className="similar-camp-provider">{camp.organizer?.name}</p>}
                  {displayDates && <p className="similar-camp-dates">
                    {camp.camp_dates && camp.camp_dates.length > 0 ? (
                      <>
                        {CampFormatUtils.formatDateRange(camp.camp_dates[0].start_date, camp.camp_dates[0].end_date, false)}
                        {camp.camp_dates.length > 1 && (
                          <span className="more-dates-indicator"> ...and {camp.camp_dates.length - 1} more</span>
                        )}
                      </>
                    ) : (
                      ''
                    )}
                  </p>}
                  {displayRating && camp.organizer?.google_rating && camp.organizer?.google_rating_count && camp.organizer?.google_maps_place_id && (
                    <div className="similar-camps-rating">
                      <GoogleRating rating={camp.organizer?.google_rating} reviewCount={camp.organizer?.google_rating_count} googlePlacesId={camp.organizer?.google_maps_place_id} showProvider={false} showLink={false} />
                    </div>
                  )}
                </div>
                <p className="similar-camp-price">
                  {CampFormatUtils.getPriceDisplay(camp)}
                </p>
              </div>
            </div>
          ))}
        </div>

        <button 
          className="similar-camps-nav similar-camps-nav--right"
          onClick={scrollRight}
        >
          <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
};

export default SimilarCamps;
